/**
 * Session-scoped, per-tab snapshot of a list/table route's search params, keyed
 * on `pathname` alone (never the query string) so a project-scoped route (the
 * ids live in the path) never restores into a different project.
 *
 * URL search params remain the source of truth — this only rehydrates a URL
 * that arrived empty. `sessionStorage` throws in Safari private mode and when
 * site data is blocked, so every access is wrapped in `try/catch` and degrades
 * to "no persistence" rather than breaking the page.
 */
export const LIST_VIEW_STATE_PREFIX = 'soji:list-view-state:';

/**
 * Reserved search key a "return to this list" link sets to ask
 * `useListViewState` to restore — the only signal that distinguishes an
 * intentional return (open a row, come back) from any other bare arrival
 * (sidebar, a shared/pasted link), which the router can't otherwise tell
 * apart: both are just "navigate to this pathname with no search." Never
 * survives into the restored URL — the restore replaces `search` wholesale
 * with the stored snapshot, which never contains this key.
 */
export const LIST_VIEW_RETURN_KEY = '_lvReturn';

function storageKey(pathname: string): string {
    return `${LIST_VIEW_STATE_PREFIX}${pathname}`;
}

/** Returns the stored snapshot for `pathname`, or `undefined` if absent, malformed, or storage is unavailable. */
export function readListViewSnapshot(
    pathname: string,
): Record<string, unknown> | undefined {
    try {
        const raw = sessionStorage.getItem(storageKey(pathname));
        if (!raw) return undefined;

        const parsed: unknown = JSON.parse(raw);
        if (
            typeof parsed !== 'object' ||
            parsed === null ||
            Array.isArray(parsed)
        ) {
            return undefined;
        }

        return parsed as Record<string, unknown>;
    } catch {
        // Malformed stored JSON, or sessionStorage.getItem threw — no snapshot.
        return undefined;
    }
}

/**
 * Stores `search` for `pathname`, dropping `undefined` values first. Writes
 * nothing (and deletes any existing key) when the result would be empty.
 */
export function writeListViewSnapshot(
    pathname: string,
    search: Record<string, unknown>,
): void {
    const defined: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(search)) {
        if (value !== undefined) {
            defined[key] = value;
        }
    }

    try {
        if (Object.keys(defined).length === 0) {
            sessionStorage.removeItem(storageKey(pathname));
            return;
        }
        sessionStorage.setItem(storageKey(pathname), JSON.stringify(defined));
    } catch {
        // Storage blocked or full — degrade to "no persistence".
    }
}

/** Removes any stored snapshot for `pathname` — used by an explicit "Reset filters" affordance. */
export function clearListViewSnapshot(pathname: string): void {
    try {
        sessionStorage.removeItem(storageKey(pathname));
    } catch {
        // Storage blocked — nothing to clear.
    }
}
